/**
 * P7-x — the guest menu, cached in IndexedDB.
 *
 * A QR guest's first wait is `GET /menus/guest`: the biggest payload in the
 * journey, and it changes maybe twice a day. Rendering the previous copy while
 * the network answers turns that wait into nothing.
 *
 * Why IndexedDB and not `localStorage`:
 *  - Synchronous: parsing a 100 KB menu on the main thread is paid during the
 *    first paint, precisely the frame this exists to protect.
 *  - A ~5 MB origin-wide budget already shared with the session, the cart and
 *    i18next; a menu with descriptions in two locales can crowd it, and a quota
 *    error there is what silently drops the *cart*.
 *  - Strings only, so every read re-parses. IndexedDB structured-clones.
 *
 * WHAT MAY LIVE HERE — nothing but the public menu. This database is shared by
 * every guest who uses this device (a family passing one phone around, a
 * borrowed handset, a hotel tablet). A session id, a guest token, a joining PIN
 * or a cart belongs instead in `localStorage` behind `clearGuestState()`, which
 * the scan flow wipes when the table changes. `sanitizeSections()` below makes
 * that structural: it copies an allow-list, so a per-guest key the API might
 * one day add to the guest-menu response is dropped rather than persisted.
 *
 * A CACHED PRICE IS DISPLAY-ONLY. The server prices what it charges;
 * nothing read out of here is ever sent back in an order.
 */

/** One item as `GET /menus/guest` returns it — money as a fixed-scale string. */
export interface GuestMenuItem {
  id: string;
  categoryId: string | null;
  type: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  /** Decimal string, e.g. `"12.500"`. Display and arithmetic-in-minor-units only. */
  basePrice: string;
  taxRate: string;
  isSoldOut: boolean;
  stationId: string | null;
}

/** `GET /menus/guest` groups the live menu by category, already localized. */
export interface GuestMenuSection {
  id: string;
  name: string;
  items: GuestMenuItem[];
}

export interface CachedMenu {
  sections: GuestMenuSection[];
  /** Epoch ms the copy was written — what the "saved menu" notice reads. */
  cachedAt: number;
}

const DB_NAME = 'tarabeeza-guest-menu';
const DB_VERSION = 1;
const STORE = 'menus';

/**
 * A menu older than this is not shown at all: likelier a menu from a holiday
 * two weeks ago than a useful head start, and a wrong menu is worse than a
 * spinner. Well inside the window a venue would reprice in.
 */
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Normalized exactly as `menus.controller.ts#resolveLocale` does, because the
 * server keys its translation rows by the primary subtag: `ar-SA` and `ar` are
 * the same response and must be the same cache entry, while `ar` and `en` must
 * never be. An Arabic guest served the English cache is the bug this prevents.
 */
export function menuLocale(raw: string | null | undefined): string {
  return (raw ?? 'en').split(';')[0].trim().split('-')[0].toLowerCase() || 'en';
}

/**
 * Tenant is in the key as well as branch. Branch ids are cuids and collisions
 * are not the worry — a device that scanned venue A and then venue B is, and a
 * key that names its tenant cannot answer for the wrong one.
 */
function cacheKey(tenantId: string | null, branchId: string, locale: string): string {
  return `${tenantId ?? '-'}|${branchId}|${menuLocale(locale)}`;
}

// --------------------------------------------------------------- the store

/**
 * `null` once IndexedDB is known to be unusable — Safari private mode, a
 * hardened browser, a corrupt database. Every function below then degrades to
 * "no cache": the screens still work, they just wait for the network.
 */
let dbPromise: Promise<IDBDatabase | null> | null = null;

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      request.onupgradeneeded = () => {
        const db = request.result;
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => resolve(null);
      // A tab left open across a version bump would otherwise hang this promise.
      request.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

function run<T>(mode: IDBTransactionMode, work: (store: IDBObjectStore) => IDBRequest<T>) {
  return openDb().then(
    (db) =>
      new Promise<T | null>((resolve) => {
        if (!db) return resolve(null);
        try {
          const tx = db.transaction(STORE, mode);
          const request = work(tx.objectStore(STORE));
          request.onsuccess = () => resolve(request.result ?? null);
          request.onerror = () => resolve(null);
          tx.onabort = () => resolve(null);
        } catch {
          resolve(null);
        }
      }),
  );
}

// --------------------------------------------------------------- allow-list

const asString = (value: unknown): string => (typeof value === 'string' ? value : '');
const asNullableString = (value: unknown): string | null =>
  typeof value === 'string' && value.length > 0 ? value : null;

/**
 * The allow-list guard described at the top of the file. It also means a
 * malformed response cannot poison the store with something the screens would
 * then have to defend against on every render.
 */
export function sanitizeSections(input: unknown): GuestMenuSection[] {
  if (!Array.isArray(input)) return [];
  const sections: GuestMenuSection[] = [];
  for (const raw of input) {
    if (!raw || typeof raw !== 'object') continue;
    const section = raw as Record<string, unknown>;
    const id = asString(section.id);
    if (!id) continue;
    const items: GuestMenuItem[] = [];
    for (const rawItem of Array.isArray(section.items) ? section.items : []) {
      if (!rawItem || typeof rawItem !== 'object') continue;
      const item = rawItem as Record<string, unknown>;
      const itemId = asString(item.id);
      if (!itemId) continue;
      items.push({
        id: itemId,
        categoryId: asNullableString(item.categoryId),
        type: asString(item.type),
        name: asString(item.name),
        description: asNullableString(item.description),
        imageUrl: asNullableString(item.imageUrl),
        // Money stays the string the server sent — parsing it to a float here
        // would bake a rounding decision into the cache itself.
        basePrice: typeof item.basePrice === 'number' ? String(item.basePrice) : asString(item.basePrice),
        taxRate: typeof item.taxRate === 'number' ? String(item.taxRate) : asString(item.taxRate),
        isSoldOut: item.isSoldOut === true,
        stationId: asNullableString(item.stationId),
      });
    }
    sections.push({ id, name: asString(section.name), items });
  }
  return sections;
}

// ------------------------------------------------------------------ public

export async function readCachedMenu(
  tenantId: string | null,
  branchId: string,
  locale: string,
): Promise<CachedMenu | null> {
  const key = cacheKey(tenantId, branchId, locale);
  const entry = await run<CachedMenu>('readonly', (store) => store.get(key));
  if (!entry || !Array.isArray(entry.sections)) return null;
  if (!Number.isFinite(entry.cachedAt) || Date.now() - entry.cachedAt > MAX_AGE_MS) {
    void run('readwrite', (store) => store.delete(key));
    return null;
  }
  return entry;
}

export async function writeCachedMenu(
  tenantId: string | null,
  branchId: string,
  locale: string,
  sections: GuestMenuSection[],
): Promise<void> {
  const entry: CachedMenu = { sections: sanitizeSections(sections), cachedAt: Date.now() };
  await run('readwrite', (store) => store.put(entry, cacheKey(tenantId, branchId, locale)));
}

/** Used by the language switcher's sibling concerns and by tests. */
export async function clearMenuCache(): Promise<void> {
  await run('readwrite', (store) => store.clear());
}
